import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, unref } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import httpClient from '../httpClient';
import type { PaginatedResponse, ScribeSession } from '@/types';

export interface SessionsFilters {
  search?: MaybeRefOrGetter<string>;
  status?: MaybeRefOrGetter<string[]>;
  client?: MaybeRefOrGetter<'linked' | 'unlinked' | null>;
  /** Inclusive ISO date-time lower bound (filters on created_at). */
  createdAtFrom?: MaybeRefOrGetter<string | null>;
  /** Inclusive ISO date-time upper bound (filters on created_at). */
  createdAtTo?: MaybeRefOrGetter<string | null>;
  perPage?: number;
}

function read<T>(v: MaybeRefOrGetter<T> | undefined): T | undefined {
  return typeof v === 'function' ? (v as () => T)() : unref(v);
}

/**
 * Server-side, paginated session list. All filtering (search, status, client,
 * date range) happens in the database; the query refetches whenever any filter
 * changes and pages are fetched on demand.
 */
export const useSessionsInfiniteQuery = (filters: SessionsFilters = {}) => {
  const perPage = filters.perPage ?? 30;

  const params = computed(() => {
    const status = read(filters.status) ?? [];
    return {
      perPage,
      search: read(filters.search)?.trim() || undefined,
      status: status.length ? status.join(',') : undefined,
      client: read(filters.client) || undefined,
      createdAtFrom: read(filters.createdAtFrom) || undefined,
      createdAtTo: read(filters.createdAtTo) || undefined,
    };
  });

  const queryKey = computed(() => ['scribeSessions', 'infinite', params.value]);

  return useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await httpClient.get<PaginatedResponse<ScribeSession>>(
        '/v1/sessions',
        { params: { ...params.value, page: pageParam } },
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages
        ? lastPage.meta.page + 1
        : undefined,
    staleTime: 5000,
  });
};
